import { useState } from "react";
import Modal from "@/components/Modal";
import { Button } from "@/components/ui/button";
import LabelHint from "@/components/ui/LabelHint";
import { Ingredient } from "@/redux/services/inventory/inventory.type";
import { useCrudIngredient } from "@/hooks/inventory/useCrudIngredient";

interface IngredientFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  ingredientToEdit?: Ingredient;
  restaurantId: string;
}

const UNIT_SUGGESTIONS = [
  "kg",
  "g",
  "mg",
  "litres",
  "dl",
  "cl",
  "ml",
  "pièces",
  "tranches",
  "portions",
  "sachets",
  "gousses",
  "feuilles",
  "filets",
];

const IngredientFormModal = ({
  isOpen,
  onClose,
  ingredientToEdit,
  restaurantId,
}: IngredientFormModalProps) => {
  const isEditMode = !!ingredientToEdit;
  const { register, errors, setValue, watch, onSubmit, isLoading } =
    useCrudIngredient(onClose, restaurantId, ingredientToEdit);
  const [showUnitSuggestions, setShowUnitSuggestions] = useState(false);

  const unitValue = watch("baseUnit") ?? "";
  const filteredSuggestions = UNIT_SUGGESTIONS.filter((s) =>
    s.toLowerCase().includes(unitValue.toLowerCase())
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? "Modifier l'ingrédient" : "Ajouter un ingrédient"}
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label
            htmlFor="ingredient-name"
            className="block text-sm font-medium text-card-foreground mb-1"
          >
            Nom
          </label>
          <input
            id="ingredient-name"
            type="text"
            {...register("name")}
            className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            placeholder="Ex: Tomates"
          />
          {errors.name && (
            <p className="mt-1 text-xs text-danger">{errors.name.message}</p>
          )}
        </div>

        <div className="relative">
          <label
            htmlFor="ingredient-unit"
            className="block text-sm font-medium text-card-foreground mb-1"
          >
            Unité
            <LabelHint text="Unité atomique de mesure (ex: kg, l, pièces)." />
          </label>
          <input
            id="ingredient-unit"
            type="text"
            {...register("baseUnit")}
            onFocus={() => setShowUnitSuggestions(true)}
            onBlur={() =>
              setTimeout(() => setShowUnitSuggestions(false), 200)
            }
            className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            placeholder="Ex: kg, litres, pièces"
          />
          {showUnitSuggestions && filteredSuggestions.length > 0 && (
            <ul className="absolute z-10 w-full bg-card border border-border rounded-md shadow-lg mt-1 max-h-40 overflow-y-auto">
              {filteredSuggestions.map((suggestion) => (
                <li key={suggestion}>
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm hover:bg-muted"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setValue("baseUnit", suggestion, { shouldValidate: true });
                      setShowUnitSuggestions(false);
                    }}
                  >
                    {suggestion}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {errors.baseUnit && (
            <p className="mt-1 text-xs text-danger">{errors.baseUnit.message}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="ingredient-threshold"
            className="block text-sm font-medium text-card-foreground mb-1"
          >
            Seuil de stock faible
          </label>
          <input
            id="ingredient-threshold"
            type="text"
            inputMode="decimal"
            {...register("lowStockThreshold")}
            className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            placeholder="Ex: 5"
          />
          {errors.lowStockThreshold && (
            <p className="mt-1 text-xs text-danger">
              {errors.lowStockThreshold.message}
            </p>
          )}
        </div>

        {!isEditMode && (
          <>
            <div>
              <label
                htmlFor="ingredient-current-stock"
                className="block text-sm font-medium text-card-foreground mb-1"
              >
                Stock initial
              </label>
              <input
                id="ingredient-current-stock"
                type="text"
                inputMode="decimal"
                {...register("currentStock")}
                className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Ex: 10"
              />
              {errors.currentStock && (
                <p className="mt-1 text-xs text-danger">
                  {errors.currentStock.message}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="ingredient-cost-per-unit"
                className="block text-sm font-medium text-card-foreground mb-1"
              >
                Coût par unité
                <LabelHint text="Coût pour une unité de base. Mis à jour automatiquement à partir des réceptions de bons de commande." />
              </label>
              <input
                id="ingredient-cost-per-unit"
                type="text"
                inputMode="decimal"
                {...register("costPerUnit")}
                className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Ex: 1500"
              />
              {errors.costPerUnit && (
                <p className="mt-1 text-xs text-danger">
                  {errors.costPerUnit.message}
                </p>
              )}
            </div>
          </>
        )}

        <div className="flex justify-end pt-2">
          <Button type="submit" isLoading={isLoading} size="sm">
            Enregistrer
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default IngredientFormModal;
